import RBush from "rbush";
import { bbox as turfBbox, length as turfLength, lineString } from "@turf/turf";
import { coordKey } from "./geo";

/**
 * Normalize OSM `oneway` tags.
 * @return {Number} 1 = travel only along coordinate order, -1 = only against it, 0 = both ways
 */
export function normalizeOneway(value) {
  const v = String(value ?? "").trim().toLowerCase();
  if (["yes", "true", "1"].includes(v)) {
    return 1;
  }
  if (["-1", "reverse"].includes(v)) {
    return -1;
  }
  return 0;
}

/**
 * Build a spatial + topological index of road LineStrings.
 * @param {Object} roads GeoJSON FeatureCollection of LineStrings
 */
export function buildRoadIndex(roads) {
  const ways = [];
  const vertices = new Map();
  const tree = new RBush();
  const items = [];

  for (const feature of roads.features || []) {
    const geometry = feature.geometry;
    if (!geometry || geometry.type !== "LineString" || geometry.coordinates.length < 2) {
      continue;
    }
    const coords = geometry.coordinates;
    const feat = lineString(coords);
    const way = {
      id: ways.length,
      properties: feature.properties || {},
      coords,
      feature: feat,
      lengthM: turfLength(feat, { units: "meters" }),
      oneway: normalizeOneway(feature.properties && feature.properties.oneway),
      name: (feature.properties && feature.properties.name) || null
    };
    ways.push(way);

    const [minX, minY, maxX, maxY] = turfBbox(feat);
    items.push({ minX, minY, maxX, maxY, way });

    // index every vertex, not just endpoints: OSM ways often branch off the middle of another way
    coords.forEach((coord, index) => {
      const key = coordKey(coord);
      if (!vertices.has(key)) {
        vertices.set(key, []);
      }
      vertices.get(key).push({ way, index });
    });
  }
  tree.load(items);

  return {
    ways,
    /** Ways whose bbox intersects [minX, minY, maxX, maxY]. */
    search([minX, minY, maxX, maxY]) {
      return tree.search({ minX, minY, maxX, maxY }).map((item) => item.way);
    },
    /** Ways with a vertex at the given coordinate, with the vertex index. */
    connectionsAt(coord) {
      return vertices.get(coordKey(coord)) || [];
    }
  };
}
